import { apiClient } from './api/client';
import { User, LoginDto, RegisterDto } from '@/types/user.types';

export const authService = {
  async login(data: LoginDto): Promise<{ user: User }> {
    try {
      console.log('📝 authService.login - Envoi de la requête:', { email: data.email });
      
      // Validation des données avant envoi
      if (!data.email || !data.password) {
        throw new Error('Email et mot de passe requis');
      }
      
      const response = await apiClient.post<{ success: boolean; user: User; requestId?: string }>('/auth/login', data);
      console.log('✅ authService.login - Réponse reçue:', response);
      console.log('   Type de réponse:', typeof response);
      console.log('   Clés de la réponse:', response ? Object.keys(response) : 'null');
      
      if (!response) {
        console.error('❌ Réponse null ou undefined');
        throw new Error('Erreur de connexion. La réponse du serveur est invalide.');
      }
      
      // Vérifier la structure de la réponse
      if (response && typeof response === 'object') {
        // Cas 1: { success: true, user: {...} }
        if ('success' in response && response.success && 'user' in response && response.user) {
          console.log('✅ Structure 1 détectée: { success: true, user: {...} }');
          return { user: response.user };
        }
        
        // Cas 2: { user: {...} } (sans success)
        if ('user' in response && response.user) {
          console.log('✅ Structure 2 détectée: { user: {...} }');
          return { user: response.user };
        }
        
        // Cas 3: La réponse est directement l'objet user (peu probable mais possible)
        if ('id' in response && 'email' in response && 'fullName' in response) {
          console.log('✅ Structure 3 détectée: user direct');
          return { user: response as User };
        }
      }
      
      console.error('❌ Structure de réponse inattendue:', response);
      throw new Error('Erreur de connexion. La réponse du serveur est invalide.');
    } catch (error: any) {
      console.error('❌ Erreur authService.login:', error);
      console.error('   Type:', error?.constructor?.name);
      console.error('   Message:', error?.message);
      console.error('   Code:', error?.code);
      console.error('   Response:', error?.response);
      
      // Gestion des erreurs réseau (backend non démarré, CORS, etc.)
      if (!error.response) {
        const networkError = error.code === 'ECONNREFUSED' || 
                            error.code === 'ERR_NETWORK' ||
                            error.message?.includes('Network Error') || 
                            error.message?.includes('Failed to fetch') ||
                            error.message?.includes('Network request failed');
        if (networkError) {
          const customError = new Error('Impossible de se connecter au serveur. Vérifiez que le serveur backend est démarré.');
          (customError as any).status = 0;
          (customError as any).isNetworkError = true;
          throw customError;
        }
      }
      
      // Gestion des erreurs de parsing JSON
      if (error.message?.includes('JSON') || error.message?.includes('parse') || error.message?.includes('Unexpected token')) {
        const customError = new Error('Erreur de connexion. La réponse du serveur est invalide.');
        (customError as any).status = 500;
        throw customError;
      }
      
      // Extraire le message d'erreur du serveur
      const errorMessage = error.response?.data?.error || 
                          error.response?.data?.message || 
                          error.message || 
                          'Erreur lors de la connexion';
      const statusCode = error.response?.status || 500;
      
      console.error('   Status:', statusCode);
      console.error('   Message:', errorMessage);
      console.error('   Response data:', error.response?.data);
      console.error('   Request ID:', error.response?.data?.requestId);
      
      // Créer une erreur avec le message du serveur
      const customError = new Error(errorMessage);
      (customError as any).status = statusCode;
      (customError as any).requestId = error.response?.data?.requestId;
      throw customError;
    }
  },

  async register(data: RegisterDto): Promise<{ user: User }> {
    try {
      console.log('📝 authService.register - Envoi de la requête:', { email: data.email, fullName: data.fullName });
      const response = await apiClient.post<{ success: boolean; user: User }>('/auth/register', data);
      console.log('✅ authService.register - Réponse reçue:', response);
      
      if (!response || !response.user) {
        throw new Error('Réponse invalide du serveur');
      }
      return { user: response.user };
    } catch (error: any) {
      console.error('❌ Erreur authService.register:', error);
      console.error('   Status:', error.response?.status);
      console.error('   Message:', error.response?.data?.error || error.message);
      console.error('   Response data:', error.response?.data);
      
      // Extraire le message d'erreur du serveur
      const errorMessage = error.response?.data?.error || error.message || 'Erreur lors de l\'inscription';
      const customError = new Error(errorMessage);
      (customError as any).status = error.response?.status;
      throw customError;
    }
  },

  async logout(): Promise<void> {
    return await apiClient.post('/auth/logout');
  },

  async getCurrentUser(): Promise<User> {
    try {
      console.log('📝 authService.getCurrentUser - Vérification de l\'authentification...');
      const response = await apiClient.get<User>('/auth/me');
      console.log('✅ authService.getCurrentUser - Utilisateur connecté:', response.email);
      return response;
    } catch (error: any) {
      // Pour /me, une erreur 401 est normale si l'utilisateur n'est pas connecté
      // On la re-throw pour que AuthContext puisse gérer
      if (error.response?.status === 401) {
        console.log('ℹ️ authService.getCurrentUser - Non authentifié (normal)');
        throw error;
      }
      
      console.error('❌ Erreur authService.getCurrentUser:', error);
      console.error('   Status:', error.response?.status);
      console.error('   Message:', error.response?.data?.error || error.message);
      console.error('   Response data:', error.response?.data);
      
      const errorMessage = error.response?.data?.error || error.message || 'Erreur lors de la récupération de l\'utilisateur';
      const customError = new Error(errorMessage);
      (customError as any).status = error.response?.status;
      throw customError;
    }
  },

  async getAllUsers(): Promise<User[]> {
    try {
      const response = await apiClient.get<User[]>('/auth/users');
      return response;
    } catch (error: any) {
      console.error('❌ Erreur authService.getAllUsers:', error);
      const errorMessage = error.response?.data?.error || error.message || 'Erreur lors de la récupération des utilisateurs';
      throw new Error(errorMessage);
    }
  },

  async createUser(data: { fullName: string; email: string; password: string; role_id?: number | null }): Promise<User> {
    try {
      const response = await apiClient.post<{ success: boolean; user: User }>('/auth/users', data);
      return response.user;
    } catch (error: any) {
      console.error('❌ Erreur authService.createUser:', error);
      const errorMessage = error.response?.data?.error || error.message || 'Erreur lors de la création de l\'utilisateur';
      throw new Error(errorMessage);
    }
  },

  async updateUser(userId: number, data: { fullName?: string; email?: string; role_id?: number | null }): Promise<User> {
    try {
      const response = await apiClient.put<{ success: boolean; user: User }>(`/auth/users/${userId}`, data);
      return response.user;
    } catch (error: any) {
      console.error('❌ Erreur authService.updateUser:', error);
      const errorMessage = error.response?.data?.error || error.message || 'Erreur lors de la mise à jour de l\'utilisateur';
      throw new Error(errorMessage);
    }
  },

  async deleteUser(userId: number): Promise<void> {
    try {
      await apiClient.delete(`/auth/users/${userId}`);
    } catch (error: any) {
      console.error('❌ Erreur authService.deleteUser:', error);
      const errorMessage = error.response?.data?.error || error.message || 'Erreur lors de la suppression de l\'utilisateur';
      throw new Error(errorMessage);
    }
  },

  async updateUserRole(userId: number, roleId: number | null): Promise<User> {
    try {
      const response = await apiClient.put<User>(`/auth/users/${userId}/role`, { role_id: roleId });
      return response;
    } catch (error: any) {
      console.error('❌ Erreur authService.updateUserRole:', error);
      const errorMessage = error.response?.data?.error || error.message || 'Erreur lors de la mise à jour du rôle';
      throw new Error(errorMessage);
    }
  },
};

